import React, { useEffect, useMemo, useRef, useState } from "react";
import { SYM, withEllipsis } from "../utils/textSymbols";
import { APPROVAL_BELL_SLOTS } from "../components/ApprovalBell";
import "./GroceriesPage.css";
import aisleLookup from "../data/groceryAisleLookup.json";

const STORAGE_KEY = "family-dashboard-paprika-groceries-v1";
const SUGGESTIONS_KEY = "family-dashboard-paprika-grocery-suggestions-v1";

const DEFAULT_AISLE = "Miscellaneous";
const DEFAULT_AISLE_UID = "5CB0B42660589925C804543D433BA0764DAB859487E5A9ED855481DCBFEC1F06";

function safeParseJSON(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function makeUid() {
  try {
    return crypto.randomUUID().toUpperCase();
  } catch {
    // Fallback: generate a UUID-like string (Paprika mainly needs a unique identifier).
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
      .replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      })
      .toUpperCase();
  }
}

function lookupAisleByKeyword(itemName) {
  const lower = String(itemName || "").toLowerCase().trim();
  if (!lower) return null;
  // Check multi-word phrases first (longer matches take precedence)
  const phrases = ["ice cream", "baking powder", "baking soda"];
  for (const phrase of phrases) {
    if (lower.includes(phrase)) {
      const match = aisleLookup[phrase];
      if (match) return match;
    }
  }
  // Then check each word
  const words = lower.split(/\s+/);
  for (const word of words) {
    const match = aisleLookup[word];
    if (match) return match;
  }
  return null;
}

function groupByAisle(items, aisleNameByUid) {
  const map = new Map();
  for (const it of items) {
    const resolved =
      (typeof it.aisle === "string" && it.aisle.trim()) ||
      (it.aisle_uid && aisleNameByUid?.get?.(it.aisle_uid)) ||
      "Unassigned";
    const aisle = String(resolved).trim() || "Unassigned";
    if (!map.has(aisle)) map.set(aisle, []);
    map.get(aisle).push(it);
  }
  // Stable-ish sort to mimic Paprika ordering
  const aisles = Array.from(map.keys()).sort((a, b) => a.localeCompare(b));
  return aisles.map((aisle) => ({
    aisle,
    items: map.get(aisle).slice().sort((a, b) => (a.order_flag ?? 0) - (b.order_flag ?? 0))
  }));
}

export default function GroceriesPage({ apiBase }) {
  const [cache, setCache] = useState(null);
  const [selectedListUid, setSelectedListUid] = useState("");
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [swipeOpenUid, setSwipeOpenUid] = useState(null);
  const swipeStartXRef = useRef(0);
  const swipeUidRef = useRef(null);
  const swipeDraggingRef = useRef(false);

  const [editItem, setEditItem] = useState(null); // { ...item }
  const [editQuantity, setEditQuantity] = useState("");
  const [editIngredient, setEditIngredient] = useState("");
  const [editInstruction, setEditInstruction] = useState("");
  const [editAisle, setEditAisle] = useState("");
  const [editPurchased, setEditPurchased] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const [adding, setAdding] = useState(false);
  const [addIngredient, setAddIngredient] = useState("");
  const [addQuantity, setAddQuantity] = useState("1");
  const [addInstruction, setAddInstruction] = useState("");
  const [addAisle, setAddAisle] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState(null);

  const [quickAddText, setQuickAddText] = useState("");
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddActiveIndex, setQuickAddActiveIndex] = useState(0);
  const quickAddRef = useRef(null);

  useEffect(() => {
    const parsed = safeParseJSON(localStorage.getItem(STORAGE_KEY));
    if (!parsed?.version) return;
    setCache(parsed);
    const lists = parsed.groceryLists || [];
    if (!selectedListUid && lists.length > 0) {
      const defaultList = lists.find((l) => l.is_default) || lists[0];
      setSelectedListUid(defaultList?.uid || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedGroceryLists = useMemo(() => {
    const lists = Array.isArray(cache?.groceryLists) ? cache.groceryLists : [];
    const isMy = (name) => String(name || "").trim().toLowerCase() === "my grocery list";
    const my = lists.find((l) => isMy(l?.name));
    const rest = lists
      .filter((l) => !isMy(l?.name))
      .slice()
      .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
    return my ? [my, ...rest] : rest;
  }, [cache]);

  const allGroceries = useMemo(() => cache?.groceries || [], [cache]);
  const groceryAisles = Array.isArray(cache?.groceryAisles) ? cache.groceryAisles : null;
  const apiIngredients = Array.isArray(cache?.groceryIngredients)
    ? cache.groceryIngredients
    : null;

  const selectedItems = useMemo(() => {
    if (!selectedListUid) return [];
    return allGroceries.filter((it) => it.list_uid === selectedListUid && !it.deleted);
  }, [allGroceries, selectedListUid]);

  const aisleUidByName = useMemo(() => {
    const map = new Map();
    if (groceryAisles) {
      for (const a of groceryAisles) {
        const name = String(a?.name || a?.aisle || "").trim();
        const uid = a?.uid || a?.aisle_uid || null;
        if (name && uid) map.set(name, uid);
      }
    }
    return map;
  }, [groceryAisles]);

  const aisleNameByUid = useMemo(() => {
    const map = new Map();
    if (groceryAisles) {
      for (const a of groceryAisles) {
        const uid = a?.uid || a?.aisle_uid || null;
        const name = String(a?.name || a?.aisle || "").trim();
        if (uid && name) map.set(uid, name);
      }
    }
    return map;
  }, [groceryAisles]);

  const itemMetaByName = useMemo(() => {
    const map = new Map();
    for (const it of allGroceries) {
      if (!it) continue;
      const name = String(it.ingredient || it.name || "").trim();
      if (!name) continue;
      // Build "My Items" from *all* history (including deleted), since Paprika may keep
      // the best aisle metadata on older/deleted entries.
      const aisle = typeof it.aisle === "string" ? it.aisle.trim() : "";
      const aisle_uid = it.aisle_uid || (aisle ? aisleUidByName.get(aisle) : null) || null;
      const prev = map.get(name) || null;
      const candidate = {
        aisle,
        aisle_uid,
        deleted: !!it.deleted
      };
      if (!prev) {
        map.set(name, candidate);
        continue;
      }
      const score = (m) =>
        (m.aisle ? 4 : 0) + (m.aisle_uid ? 2 : 0) + (m.deleted ? 0 : 1);
      if (score(candidate) >= score(prev)) {
        map.set(name, candidate);
      }
    }
    return map;
  }, [allGroceries, aisleUidByName]);

  const myItems = useMemo(() => {
    return Array.from(itemMetaByName.keys()).sort((a, b) => a.localeCompare(b));
  }, [itemMetaByName]);

  useEffect(() => {
    try {
      localStorage.setItem(
        SUGGESTIONS_KEY,
        JSON.stringify({ version: 1, updatedAt: Date.now(), myItems })
      );
    } catch {
      // ignore
    }
  }, [myItems]);

  const quickSuggestions = useMemo(() => {
    const q = quickAddText.trim().toLowerCase();
    if (!q) return { my: [], ingredients: [] };

    const filter = (arr) =>
      arr
        .filter((s) => s.toLowerCase().includes(q))
        .slice(0, 12);

    const my = filter(myItems);
    let ingredients = [];
    if (apiIngredients) {
      const vals = apiIngredients
        .map((x) => (typeof x === "string" ? x : x?.ingredient || x?.name || ""))
        .map((s) => String(s).trim())
        .filter(Boolean);
      ingredients = filter(
        Array.from(new Set(vals)).sort((a, b) => a.localeCompare(b))
      );
    } else {
      ingredients = my.slice(0, 12);
    }
    return { my, ingredients };
  }, [quickAddText, myItems, apiIngredients]);

  const flattenedSuggestions = useMemo(() => {
    const out = [];
    for (const s of quickSuggestions.my) {
      const meta = itemMetaByName.get(s) || null;
      out.push({ group: "My Items", value: s, meta });
    }
    for (const s of quickSuggestions.ingredients) {
      out.push({ group: "Ingredients", value: s, meta: null });
    }
    return out.slice(0, 24);
  }, [quickSuggestions, itemMetaByName]);

  const aisleOptions = useMemo(() => {
    // Prefer full aisles list from API; fallback to aisles seen in current list.
    if (groceryAisles) {
      const options = groceryAisles
        .map((a) => ({
          aisle: String(a?.name || a?.aisle || "").trim(),
          aisle_uid: a?.uid || a?.aisle_uid || ""
        }))
        .filter((a) => a.aisle)
        .sort((a, b) => a.aisle.localeCompare(b.aisle));
      return options;
    }
    const byAisle = new Map();
    for (const it of selectedItems) {
      const aisle = (it.aisle || "").trim();
      if (!aisle) continue;
      if (!byAisle.has(aisle)) byAisle.set(aisle, it.aisle_uid || "");
    }
    return Array.from(byAisle.entries())
      .map(([aisle, aisle_uid]) => ({ aisle, aisle_uid }))
      .sort((a, b) => a.aisle.localeCompare(b.aisle));
  }, [groceryAisles, selectedItems]);

  useEffect(() => {
    if (!adding) return;
    if (addAisle) return;
    if (aisleOptions.length > 0) setAddAisle(aisleOptions[0].aisle);
  }, [adding, addAisle, aisleOptions]);

  const grouped = useMemo(() => groupByAisle(selectedItems, aisleNameByUid), [selectedItems, aisleNameByUid]);

  const syncFromPaprika = async () => {
    setSyncLoading(true);
    setSyncError(null);
    try {
      const res = await fetch(`${apiBase}/api/GetPaprikaGroceryData`, {
        credentials: "include"
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Sync failed (${res.status})`);
      }
      const nextCache = {
        version: 1,
        fetchedAt: Date.now(),
        groceryLists: data.groceryLists || [],
        groceries: data.groceries || [],
        groceryAisles: Array.isArray(data.groceryAisles) ? data.groceryAisles : null,
        groceryIngredients: Array.isArray(data.groceryIngredients) ? data.groceryIngredients : null
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextCache));
      setCache(nextCache);

      const lists = nextCache.groceryLists || [];
      if (!selectedListUid) {
        const defaultList = lists.find((l) => l.is_default) || lists[0];
        setSelectedListUid(defaultList?.uid || "");
      }
    } catch (e) {
      setSyncError(e?.message || "Sync failed.");
    } finally {
      setSyncLoading(false);
    }
  };

  // Initial sync when user switches to Groceries tab (component mount).
  useEffect(() => {
    syncFromPaprika();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const startEdit = (item) => {
    setEditError(null);
    setEditItem(item);
    setEditQuantity(item?.quantity ?? "");
    setEditIngredient(item?.ingredient ?? item?.name ?? "");
    setEditInstruction(item?.instruction ?? "");
    setEditAisle(
      (typeof item?.aisle === "string" && item.aisle) ||
        (item?.aisle_uid && aisleNameByUid.get(item.aisle_uid)) ||
        ""
    );
    setEditPurchased(!!item?.purchased);
  };

  const togglePurchased = async (item, nextPurchased) => {
    if (!cache) return;
    const updated = { ...item, purchased: !!nextPurchased };
    const prevGroceries = cache.groceries || [];

    // Optimistic UI update.
    const optimisticallyUpdated = prevGroceries.map((it) => (it.uid === updated.uid ? updated : it));
    const optimisticCache = { ...cache, groceries: optimisticallyUpdated };
    setCache(optimisticCache);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(optimisticCache));

    try {
      const res = await fetch(`${apiBase}/api/UpdatePaprikaGroceryItems`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: [updated] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Update failed (${res.status})`);
    } catch (e) {
      // Revert on failure.
      const revertedCache = { ...cache, groceries: prevGroceries };
      setCache(revertedCache);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(revertedCache));
      setSyncError(e?.message || "Update failed.");
    }
  };

  const deleteItem = async (item) => {
    if (!cache) return;
    const prevGroceries = cache.groceries || [];
    const updated = { ...item, deleted: true };
    const nextGroceries = prevGroceries.map((it) => (it.uid === updated.uid ? updated : it));
    const optimisticCache = { ...cache, groceries: nextGroceries };
    setCache(optimisticCache);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(optimisticCache));
    setSwipeOpenUid(null);

    try {
      const res = await fetch(`${apiBase}/api/UpdatePaprikaGroceryItems`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: [updated] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Delete failed (${res.status})`);
    } catch (e) {
      const revertedCache = { ...cache, groceries: prevGroceries };
      setCache(revertedCache);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(revertedCache));
      setSyncError(e?.message || "Delete failed.");
    }
  };

  const onRowTouchStart = (uid, e) => {
    if (!e.touches?.[0]) return;
    swipeDraggingRef.current = true;
    swipeUidRef.current = uid;
    swipeStartXRef.current = e.touches[0].clientX;
  };

  const onRowTouchMove = (uid, e) => {
    if (!swipeDraggingRef.current) return;
    if (swipeUidRef.current !== uid) return;
    const x = e.touches?.[0]?.clientX;
    if (typeof x !== "number") return;
    const dx = x - swipeStartXRef.current;
    if (dx < -30) setSwipeOpenUid(uid);
    else if (dx > 30) setSwipeOpenUid(null);
  };

  const onRowTouchEnd = () => {
    swipeDraggingRef.current = false;
    swipeUidRef.current = null;
  };

  const submitQuickAdd = async (raw) => {
    if (!selectedListUid) {
      setSyncError("Select a grocery list first.");
      return;
    }
    const value = typeof raw === "string" ? raw : raw?.value;
    const meta = typeof raw === "object" && raw ? raw.meta : null;
    const text = String(value || "").trim();
    if (!text) return;

    const newItem = {
      uid: makeUid(),
      list_uid: selectedListUid,
      ingredient: text,
      name: text,
      quantity: "",
      instruction: "",
      purchased: false,
      recipe_uid: null,
      recipe: null,
      separate: false,
      order_flag: 0
    };
    // 1) Use meta from autocomplete (My Items) if available; 2) lookup by keyword; 3) Miscellaneous
    let aisle = meta?.aisle ? String(meta.aisle).trim() : "";
    let aisle_uid = meta?.aisle_uid || null;
    if (!aisle && !aisle_uid) {
      const lookedUp = lookupAisleByKeyword(text);
      if (lookedUp) {
        aisle = lookedUp.aisle;
        aisle_uid = lookedUp.aisle_uid;
      }
    }
    if (!aisle && !aisle_uid) {
      aisle = DEFAULT_AISLE;
      aisle_uid = DEFAULT_AISLE_UID;
    }
    newItem.aisle = aisle;
    newItem.aisle_uid = aisle_uid;

    setCache((prev) => {
      if (!prev) return prev;
      const next = { ...prev, groceries: [...(prev.groceries || []), newItem] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });

    setQuickAddText("");
    setQuickAddOpen(false);
    setQuickAddActiveIndex(0);
    requestAnimationFrame(() => quickAddRef.current?.focus());

    try {
      const res = await fetch(`${apiBase}/api/UpdatePaprikaGroceryItems`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: [newItem] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Add failed (${res.status})`);
    } catch (e) {
      setCache((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          groceries: (prev.groceries || []).filter((it) => it.uid !== newItem.uid)
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      });
      setSyncError(e?.message || "Add failed.");
    }
  };

  const closeEdit = () => {
    setEditItem(null);
    setEditSaving(false);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editItem) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const aisleName = String(editAisle ?? "").trim();
      const aisleMatch = aisleName ? aisleOptions.find((a) => a.aisle === aisleName) : null;
      const nextAisleUid = aisleMatch?.aisle_uid || null;

      const updated = {
        ...editItem,
        ingredient: editIngredient.trim(),
        name: editIngredient.trim(),
        quantity: String(editQuantity ?? "").trim(),
        instruction: editInstruction || "",
        purchased: !!editPurchased
      };
      if (aisleName) updated.aisle = aisleName;
      else delete updated.aisle;
      if (nextAisleUid) updated.aisle_uid = nextAisleUid;
      else delete updated.aisle_uid;

      const res = await fetch(`${apiBase}/api/UpdatePaprikaGroceryItems`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: [updated] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Update failed (${res.status})`);

      // Optimistically update local cache.
      setCache((prev) => {
        if (!prev) return prev;
        const nextGroceries = (prev.groceries || []).map((it) => (it.uid === updated.uid ? updated : it));
        const next = { ...prev, groceries: nextGroceries };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      });
      closeEdit();
    } catch (e) {
      setEditError(e?.message || "Save failed.");
    } finally {
      setEditSaving(false);
    }
  };

  const openAdd = () => {
    setAddError(null);
    if (!addAisle) setAddAisle("Miscellaneous");
    setAdding(true);
  };

  const closeAdd = () => {
    setAdding(false);
    setAddError(null);
    setAddIngredient("");
    setAddQuantity("1");
    setAddInstruction("");
    setAddAisle("");
    setAddSaving(false);
  };

  const addSubmit = async () => {
    if (!selectedListUid) {
      setAddError("Select a grocery list first.");
      return;
    }

    const ingredient = addIngredient.trim();
    const quantity = String(addQuantity ?? "").trim();
    if (!ingredient) {
      setAddError("Ingredient is required.");
      return;
    }
    if (!quantity) {
      setAddError("Quantity is required.");
      return;
    }

    const aisleMatch = aisleOptions.find((a) => a.aisle === addAisle);
    let aisle_uid = aisleMatch?.aisle_uid || null;
    let aisle = (addAisle || aisleMatch?.aisle || "").trim();
    // If user left Unassigned, try lookup by ingredient name, then Miscellaneous
    if (!aisle && !aisle_uid) {
      const lookedUp = lookupAisleByKeyword(ingredient);
      if (lookedUp) {
        aisle = lookedUp.aisle;
        aisle_uid = lookedUp.aisle_uid;
      }
    }
    if (!aisle && !aisle_uid) {
      aisle = DEFAULT_AISLE;
      aisle_uid = DEFAULT_AISLE_UID;
    }

    const newItem = {
      uid: makeUid(),
      list_uid: selectedListUid,
      ingredient,
      name: ingredient,
      quantity,
      instruction: addInstruction || "",
      purchased: false,
      recipe_uid: null,
      recipe: null,
      separate: false,
      order_flag: 0
    };
    newItem.aisle = aisle;
    newItem.aisle_uid = aisle_uid;

    setAddSaving(true);
    setAddError(null);
    try {
      const res = await fetch(`${apiBase}/api/UpdatePaprikaGroceryItems`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items: [newItem] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Add failed (${res.status})`);

      setCache((prev) => {
        if (!prev) return prev;
        const nextGroceries = [...(prev.groceries || []), newItem];
        const next = { ...prev, groceries: nextGroceries };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      });

      closeAdd();
    } catch (e) {
      setAddError(e?.message || "Add failed.");
    } finally {
      setAddSaving(false);
    }
  };

  const defaultListName = (uid) => sortedGroceryLists.find((l) => l.uid === uid)?.name || "List";
  const selectedListName = selectedListUid ? defaultListName(selectedListUid) : "Groceries";

  return (
    <div className="grocery-page">
      <div className="grocery-header">
        <div id={APPROVAL_BELL_SLOTS.groceries} className="approval-bell-slot" aria-hidden="true" />
        <h2 className="grocery-title">Groceries</h2>
        <div className="grocery-header-actions">
          <button
            type="button"
            className="grocery-refresh-btn"
            onClick={syncFromPaprika}
            disabled={syncLoading}
            aria-busy={syncLoading}
          >
            {syncLoading ? withEllipsis("Syncing") : "Refresh"}
          </button>
        </div>
      </div>

      {syncError && <div className="grocery-error">{syncError}</div>}

      {!cache && (
        <div className="grocery-empty-state">
          <p className="grocery-empty-hint">
            Grocery lists are stored locally. Click <b>Refresh</b> to sync from Paprika.
          </p>
        </div>
      )}

      {cache && (
        <>
          <div className="grocery-layout">
            <aside className="grocery-sidebar" aria-label="Grocery lists">
              <div className="grocery-sidebar-title">Grocery Lists</div>
              <div className="grocery-sidebar-items">
                {sortedGroceryLists.map((l) => {
                  const active = l.uid === selectedListUid;
                  return (
                    <button
                      key={l.uid}
                      type="button"
                      className={`grocery-list-btn ${active ? "active" : ""}`}
                      onClick={() => setSelectedListUid(l.uid)}
                      title={l.name}
                    >
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="grocery-main">
              <div className="grocery-quick-add">
                <input
                  ref={quickAddRef}
                  className="grocery-quick-add-input"
                  type="text"
                  value={quickAddText}
                  placeholder={withEllipsis("Add item")}
                  onChange={(e) => {
                    setQuickAddText(e.target.value);
                    setQuickAddOpen(true);
                    setQuickAddActiveIndex(0);
                  }}
                  onFocus={() => setQuickAddOpen(true)}
                  onBlur={() => {
                    setTimeout(() => setQuickAddOpen(false), 120);
                  }}
                  onKeyDown={(e) => {
                    if (!quickAddOpen && e.key === "ArrowDown") {
                      setQuickAddOpen(true);
                      return;
                    }
                    if (e.key === "Escape") {
                      setQuickAddOpen(false);
                      return;
                    }
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setQuickAddActiveIndex((i) =>
                        Math.min(i + 1, Math.max(0, flattenedSuggestions.length - 1))
                      );
                      return;
                    }
                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setQuickAddActiveIndex((i) => Math.max(i - 1, 0));
                      return;
                    }
                    if (e.key === "Enter") {
                      e.preventDefault();
                      // Enter should submit exactly what the user typed (not the first suggestion).
                      submitQuickAdd(quickAddText);
                    }
                  }}
                  aria-label="Quick add grocery item"
                  autoComplete="off"
                />
                {quickAddOpen &&
                  quickAddText.trim() &&
                  flattenedSuggestions.length > 0 && (
                    <div className="grocery-suggest" role="listbox">
                      {flattenedSuggestions.map((s, idx) => (
                        <button
                          key={`${s.group}-${s.value}-${idx}`}
                          type="button"
                          className={`grocery-suggest-item ${idx === quickAddActiveIndex ? "active" : ""}`}
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => submitQuickAdd(s)}
                        >
                          <span className="grocery-suggest-main">{s.value}</span>
                          <span className="grocery-suggest-sub">{s.group}</span>
                        </button>
                      ))}
                    </div>
                  )}
              </div>

              <div className="grocery-mobile-list">
                <label className="grocery-mobile-label" htmlFor="grocery-list-select">
                  {selectedListName}
                </label>
                <select
                  id="grocery-list-select"
                  className="grocery-mobile-select"
                  value={selectedListUid}
                  onChange={(e) => setSelectedListUid(e.target.value)}
                >
                  {sortedGroceryLists.map((l) => (
                    <option key={l.uid} value={l.uid}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grocery-list-name-row">
                <span className="grocery-list-name">{selectedListName}</span>
                <button type="button" className="grocery-add-btn" onClick={openAdd}>
                  + Add
                </button>
              </div>

              {selectedItems.length === 0 && (
                <div className="grocery-empty-list">
                  No items in this list yet. Click <b>+ Add</b> to create one.
                </div>
              )}

              {selectedItems.length > 0 && (
                <div className="grocery-aisles">
                  {grouped.map((group) => (
                    <div key={group.aisle} className="grocery-aisle-block">
                      <div className="grocery-aisle-header">{group.aisle}</div>
                      <div className="grocery-items">
                        {group.items.map((it) => (
                          <div
                            key={it.uid}
                            className={`grocery-swipe-row ${swipeOpenUid === it.uid ? "open" : ""}`}
                            onTouchStart={(e) => onRowTouchStart(it.uid, e)}
                            onTouchMove={(e) => onRowTouchMove(it.uid, e)}
                            onTouchEnd={onRowTouchEnd}
                          >
                            <button
                              type="button"
                              className="grocery-swipe-delete"
                              onClick={() => deleteItem(it)}
                            >
                              Delete
                            </button>
                            <div
                              className="grocery-item-row"
                              onClick={() => {
                                setSwipeOpenUid(null);
                                startEdit(it);
                              }}
                            >
                              <input
                                type="checkbox"
                                className="grocery-item-checkbox"
                                checked={!!it.purchased}
                                onChange={(e) => {
                                  const nextPurchased = e.target.checked;
                                  togglePurchased(it, nextPurchased);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Mark ${it.ingredient || it.name || "item"} as purchased`}
                              />

                              <div className="grocery-item-content">
                                <div className="grocery-item-main">
                                  <button type="button" className="grocery-item-ingredient-btn" onClick={() => startEdit(it)}>
                                    {it.ingredient || it.name}
                                  </button>
                                </div>
                                <div className="grocery-item-meta">
                                  {String(it.quantity ?? "").trim() !== "" && (
                                    <span className="grocery-item-quantity">
                                      {it.quantity}
                                    </span>
                                  )}
                                  {typeof it.recipe === "string" && it.recipe.trim() && (
                                    <span className="grocery-item-recipe">{" "}{it.recipe.trim()}</span>
                                  )}
                                </div>
                                {it.instruction && (
                                  <div className="grocery-item-notes">{it.instruction}</div>
                                )}
                              </div>

                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Edit modal */}
      {editItem && (
        <div className="grocery-modal-overlay" onClick={closeEdit} role="presentation">
          <div className="grocery-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="grocery-modal-header">
              <h3>Edit item</h3>
              <button type="button" className="grocery-modal-close" onClick={closeEdit} aria-label="Close">
                {SYM.close}
              </button>
            </div>

            <div className="grocery-modal-body">
              {editError && <div className="grocery-modal-error">{editError}</div>}

              <label className="grocery-form-label">
                Ingredient
                <input
                  className="grocery-form-input"
                  type="text"
                  value={editIngredient}
                  onChange={(e) => setEditIngredient(e.target.value)}
                />
              </label>

              <label className="grocery-form-label">
                Aisle
                {aisleOptions.length > 0 ? (
                  <select
                    className="grocery-form-select"
                    value={String(editAisle ?? "")}
                    onChange={(e) => setEditAisle(e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {aisleOptions.map((a) => (
                      <option key={a.aisle} value={a.aisle}>
                        {a.aisle}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="grocery-form-input"
                    type="text"
                    value={String(editAisle ?? "")}
                    onChange={(e) => setEditAisle(e.target.value)}
                    placeholder="Unassigned"
                  />
                )}
              </label>

              <label className="grocery-form-label">
                Quantity
                <input
                  className="grocery-form-input"
                  type="text"
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(e.target.value)}
                />
              </label>

              <label className="grocery-form-label">
                Notes
                <textarea
                  className="grocery-form-textarea"
                  rows={3}
                  value={editInstruction}
                  onChange={(e) => setEditInstruction(e.target.value)}
                />
              </label>

              <label className="grocery-form-checkbox">
                <input
                  type="checkbox"
                  checked={editPurchased}
                  onChange={(e) => setEditPurchased(e.target.checked)}
                />
                Purchased
              </label>
            </div>

            <div className="grocery-modal-actions">
              <button
                type="button"
                className="grocery-modal-btn-secondary grocery-modal-delete"
                onClick={async () => {
                  if (!editItem) return;
                  closeEdit();
                  await deleteItem(editItem);
                }}
                disabled={editSaving}
              >
                Delete
              </button>
              <button type="button" className="grocery-modal-btn-secondary" onClick={closeEdit} disabled={editSaving}>
                Cancel
              </button>
              <button type="button" className="grocery-modal-btn" onClick={saveEdit} disabled={editSaving}>
                {editSaving ? withEllipsis("Saving") : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add modal */}
      {adding && (
        <div className="grocery-modal-overlay" onClick={closeAdd} role="presentation">
          <div className="grocery-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="grocery-modal-header">
              <h3>Add grocery item</h3>
              <button type="button" className="grocery-modal-close" onClick={closeAdd} aria-label="Close">
                {SYM.close}
              </button>
            </div>

            <div className="grocery-modal-body">
              {addError && <div className="grocery-modal-error">{addError}</div>}
              <label className="grocery-form-label">
                Ingredient
                <input
                  className="grocery-form-input"
                  type="text"
                  value={addIngredient}
                  onChange={(e) => setAddIngredient(e.target.value)}
                  placeholder="e.g. eggs"
                />
              </label>

              <label className="grocery-form-label">
                Quantity
                <input
                  className="grocery-form-input"
                  type="text"
                  value={addQuantity}
                  onChange={(e) => setAddQuantity(e.target.value)}
                  placeholder="e.g. 12"
                />
              </label>

              <label className="grocery-form-label">
                Notes
                <textarea
                  className="grocery-form-textarea"
                  rows={3}
                  value={addInstruction}
                  onChange={(e) => setAddInstruction(e.target.value)}
                  placeholder="Optional notes"
                />
              </label>

              <label className="grocery-form-label">
                Aisle
                {aisleOptions.length > 0 ? (
                  <select
                    className="grocery-form-select"
                    value={addAisle}
                    onChange={(e) => setAddAisle(e.target.value)}
                  >
                    {aisleOptions.map((a) => (
                      <option key={a.aisle_uid || a.aisle} value={a.aisle}>
                        {a.aisle}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="grocery-form-input"
                    type="text"
                    value={addAisle}
                    onChange={(e) => setAddAisle(e.target.value)}
                    placeholder="e.g. Miscellaneous"
                  />
                )}
              </label>
            </div>

            <div className="grocery-modal-actions">
              <button type="button" className="grocery-modal-btn-secondary" onClick={closeAdd} disabled={addSaving}>
                Cancel
              </button>
              <button
                type="button"
                className="grocery-modal-btn"
                onClick={addSubmit}
                disabled={addSaving}
              >
                {addSaving ? withEllipsis("Adding") : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

